// Tela inicial do Motorista no app (pedido 2026-10-08: "a tela do motorista
// tem que ser melhor, mais bonita — se ele não tiver permissão pra ver
// início, mapa, veículos, eventos tem que ser uma tela mais legal").
// Substitui o Início de cliente quando accountType === "motorista": cartão
// do veículo dele (foto, status, endereço), vistoria em destaque e só os
// atalhos que o admin liberou em Permissões → Menus do app. Sem veículo
// vinculado, mostra um aviso claro em vez de uma tela vazia.
import { useMemo, useState } from "react";
import {
  Bell,
  CarFront,
  ChevronRight,
  ClipboardCheck,
  Gauge,
  Lock,
  MapPin,
  Route as RouteIcon,
  ShieldCheck,
  Unlock,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { Vehicle } from "@/types";
import { useAuth } from "@/contexts/auth-context";
import { usePermission } from "@/hooks/use-permission";
import { useAppMenuAccess } from "@/hooks/use-app-menu-access";
import { useReverseGeocode } from "@/hooks/use-reverse-geocode";
import { useBrandLogo } from "@/hooks/use-brand-logo";
import { useVehicleImages } from "@/hooks/queries";
import { useMyVehicleInspections } from "@/hooks/queries/vehicle-inspections";
import { resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";
import { LicensePlate } from "@/components/license-plate";

const relFmt = new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
const longDate = new Intl.DateTimeFormat("pt-BR", { weekday: "long", day: "numeric", month: "long" });

export function DriverHomePanel({
  vehicles,
  vehiclesLoading,
  greeting,
  statusColor,
  statusLabel,
  iconUrl,
  onOpenVehicleDetails,
  onShowOnMap,
  onOpenEventos,
  onOpenReports,
  onOpenVistoria,
}: {
  vehicles: Vehicle[];
  vehiclesLoading: boolean;
  greeting: { text: string; emoji: string };
  statusColor: (v: Vehicle) => string;
  statusLabel: (v: Vehicle) => string;
  iconUrl: (v: Vehicle) => string;
  onOpenVehicleDetails: (id: string) => void;
  onShowOnMap: (id: string) => void;
  onOpenEventos: () => void;
  onOpenReports: (id: string) => void;
  onOpenVistoria: () => void;
}) {
  const { user } = useAuth();
  const menu = useAppMenuAccess();
  const canLock = usePermission("devices", "lock");
  const canUnlock = usePermission("devices", "unlock");
  const canInspect = usePermission("vistoria", "create");
  const { displayedLogoUrl: logo } = useBrandLogo(true);

  const mine = useMemo(() => vehicles.filter((v) => !v.isTag), [vehicles]);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const v = mine.find((x) => x.id === pickedId) ?? mine[0] ?? null;

  const { data: images } = useVehicleImages(
    useMemo(() => mine.map((x) => x.id), [mine]),
    mine.length > 0,
  );
  const photo = v && images?.[v.id] ? resolveGpsDriveUrl(images[v.id]["512"] ?? images[v.id].original) : null;
  const address = useReverseGeocode(v?.hasPosition === false ? undefined : v?.lat, v?.lng, {
    stableKey: v?.id,
    priority: true,
  });

  const { data: inspections } = useMyVehicleInspections(canInspect);
  const lastInspection = useMemo(
    () => (inspections ?? []).find((i) => !v || String(i.traccarDeviceId) === v.id) ?? null,
    [inspections, v],
  );
  const inspectedToday =
    !!lastInspection && new Date(lastInspection.createdAt).toDateString() === new Date().toDateString();

  const firstName = (user?.name ?? "").split(" ")[0];
  const actions = v
    ? [
        menu.mapa && { key: "mapa", label: "Ver no mapa", icon: MapPin, tone: "from-sky-500 to-blue-600", onClick: () => onShowOnMap(v.id) },
        menu.historico && { key: "hist", label: "Percursos", icon: RouteIcon, tone: "from-violet-500 to-fuchsia-600", onClick: () => onOpenReports(v.id) },
        menu.eventos && { key: "ev", label: "Alertas", icon: Bell, tone: "from-amber-500 to-orange-600", onClick: onOpenEventos },
        (canLock || canUnlock) && {
          key: "lock",
          label: v.blocked ? "Desbloquear" : "Bloquear",
          icon: v.blocked ? Unlock : Lock,
          tone: v.blocked ? "from-emerald-500 to-green-600" : "from-rose-500 to-red-600",
          onClick: () => onOpenVehicleDetails(v.id),
        },
      ].filter(Boolean) as { key: string; label: string; icon: typeof MapPin; tone: string; onClick: () => void }[]
    : [];

  return (
    <div className="min-h-full bg-slate-50 pb-32 dark:bg-slate-950">
      {/* Cabeçalho */}
      <div
        className="relative overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-slate-900 via-slate-800 to-primary px-5 pb-20 text-white"
        style={{ paddingTop: "calc(1.25rem + env(safe-area-inset-top))" }}
      >
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-10 h-56 w-56 rounded-full bg-primary/40 blur-3xl" />
        <div className="relative flex items-center justify-between">
          <img src={logo} alt="" className="h-8 max-w-[140px] object-contain" />
          <span className="rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider backdrop-blur">
            Motorista
          </span>
        </div>
        <div className="relative mt-6">
          <div className="text-[13px] capitalize text-white/70">{longDate.format(new Date())}</div>
          <h1 className="mt-1 text-2xl font-bold leading-tight">
            {greeting.text}
            {firstName ? `, ${firstName}` : ""} <span aria-hidden>{greeting.emoji}</span>
          </h1>
          <p className="mt-1 text-[13px] text-white/75">Boa viagem e direção segura.</p>
        </div>
      </div>

      <div className="relative -mt-14 space-y-4 px-4">
        {/* Veículo */}
        {vehiclesLoading && !v ? (
          <div className="h-56 animate-pulse rounded-3xl bg-white shadow-lg dark:bg-slate-900" />
        ) : !v ? (
          <div className="rounded-3xl bg-white p-6 text-center shadow-lg dark:bg-slate-900">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-primary/10 text-primary">
              <CarFront className="h-8 w-8" />
            </div>
            <h2 className="mt-4 text-base font-bold text-slate-900 dark:text-slate-100">Nenhum veículo ainda</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Ainda não há veículo vinculado a você. Peça ao gestor da frota para vincular o seu veículo — ele
              aparece aqui na hora.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-3xl bg-white shadow-lg dark:bg-slate-900">
            <div className="relative h-36 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900">
              {photo ? (
                <img src={photo} alt={v.name} className="h-full w-full object-cover" />
              ) : (
                <img src={iconUrl(v)} alt="" className="mx-auto h-full object-contain p-4" />
              )}
              <span
                className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-bold shadow dark:bg-slate-900/95"
                style={{ color: statusColor(v) }}
              >
                <span className="h-2 w-2 rounded-full" style={{ background: statusColor(v) }} />
                {statusLabel(v)}
              </span>
              {v.blocked && (
                <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-rose-600 px-2.5 py-1 text-[11px] font-bold text-white shadow">
                  <Lock className="h-3 w-3" /> Bloqueado
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => onOpenVehicleDetails(v.id)}
              className="flex w-full items-start gap-3 p-4 text-left"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-lg font-bold text-slate-900 dark:text-slate-100">{v.name}</div>
                {v.plate && (
                  <div className="mt-0.5">
                    <LicensePlate plate={v.plate} size="sm" />
                  </div>
                )}
                <div className="mt-2 flex items-start gap-1.5 text-[12px] text-slate-500 dark:text-slate-400">
                  <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span className="line-clamp-2">{address ?? "Buscando endereço…"}</span>
                </div>
              </div>
              <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-slate-400" />
            </button>
            <div className="grid grid-cols-3 divide-x divide-slate-100 border-t border-slate-100 text-center dark:divide-slate-800 dark:border-slate-800">
              <Stat icon={Gauge} label="Velocidade" value={`${Math.round(v.speed ?? 0)} km/h`} />
              <Stat icon={ShieldCheck} label="Ignição" value={v.ignition ? "Ligada" : "Desligada"} />
              <Stat
                icon={RouteIcon}
                label="Hodômetro"
                value={v.odometer ? `${Math.round(v.odometer).toLocaleString("pt-BR")} km` : "—"}
              />
            </div>
          </div>
        )}

        {/* Troca de veículo (motorista com mais de um) */}
        {mine.length > 1 && (
          <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
            {mine.map((x) => (
              <button
                key={x.id}
                type="button"
                onClick={() => setPickedId(x.id)}
                className={cn(
                  "flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-[12px] font-semibold",
                  x.id === v?.id
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200",
                )}
              >
                <span className="h-2 w-2 rounded-full" style={{ background: statusColor(x) }} />
                {x.name}
              </button>
            ))}
          </div>
        )}

        {/* Vistoria em destaque */}
        {canInspect && (
          <button
            type="button"
            onClick={onOpenVistoria}
            className={cn(
              "relative flex w-full items-center gap-4 overflow-hidden rounded-3xl p-5 text-left text-white shadow-lg active:scale-[0.99]",
              inspectedToday
                ? "bg-gradient-to-br from-emerald-500 to-teal-600"
                : "bg-gradient-to-br from-primary to-indigo-600",
            )}
          >
            <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/15" />
            <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-white/20 backdrop-blur">
              <ClipboardCheck className="h-7 w-7" />
            </div>
            <div className="relative min-w-0 flex-1">
              <div className="text-base font-bold">
                {inspectedToday ? "Vistoria de hoje feita" : "Fazer vistoria do veículo"}
              </div>
              <div className="mt-0.5 flex items-center gap-1 text-[12px] text-white/85">
                {lastInspection ? (
                  <>
                    {lastInspection.hasIssues ? (
                      <AlertTriangle className="h-3.5 w-3.5" />
                    ) : (
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    )}
                    Última: {relFmt.format(new Date(lastInspection.createdAt))}
                    {lastInspection.hasIssues ? " · com problemas" : " · tudo ok"}
                  </>
                ) : (
                  "Confira pneus, luzes, óleo e tire as fotos"
                )}
              </div>
            </div>
            <ChevronRight className="relative h-5 w-5 shrink-0" />
          </button>
        )}

        {/* Atalhos liberados */}
        {actions.length > 0 && (
          <div>
            <div className="mb-2 px-1 text-[12px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Atalhos
            </div>
            <div className="grid grid-cols-2 gap-3">
              {actions.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  onClick={a.onClick}
                  className="flex items-center gap-3 rounded-2xl bg-white p-3.5 text-left shadow-sm active:scale-[0.98] dark:bg-slate-900"
                >
                  <span className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br text-white", a.tone)}>
                    <a.icon className="h-5 w-5" />
                  </span>
                  <span className="text-[13px] font-semibold text-slate-800 dark:text-slate-100">{a.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {!canInspect && actions.length === 0 && v && (
          <p className="px-2 text-center text-[12px] text-slate-500 dark:text-slate-400">
            Toque no veículo para ver os detalhes. Outras funções podem ser liberadas pelo gestor da frota.
          </p>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }: { icon: typeof Gauge; label: string; value: string }) {
  return (
    <div className="px-2 py-3">
      <Icon className="mx-auto h-4 w-4 text-slate-400" />
      <div className="mt-1 text-[13px] font-bold text-slate-900 dark:text-slate-100">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
    </div>
  );
}
